/**
 * Payments Page (T105)
 * List payments with filters: date range, payment method, customer, status
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Search, Filter, Calendar, Edit, FileText, CreditCard, User } from 'lucide-react';
import { useToast } from '../components/ui';
import { TableListSkeleton } from '../components/ui/SkeletonLoaders';
import {
  usePayments,
  formatCurrency,
  formatDate,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_CONFIG,
  type PaymentMethod,
  type PaymentStatus,
  type Payment,
} from '../hooks/usePayments';
import PaymentRecordModal from '../components/Payments/PaymentRecordModal';

const METHODS: PaymentMethod[] = [
  'CREDIT_CARD',
  'DEBIT_CARD',
  'BANK_TRANSFER',
  'CASH',
  'CHECK',
  'APPLE_PAY',
  'GOOGLE_PAY',
  'OTHER',
];

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  // Modal state
  const [editPayment, setEditPayment] = useState<Payment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, error } = usePayments({
    page,
    limit: 20,
    status: statusFilter,
    method: methodFilter,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const payments = data?.payments || [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  // Client-side search filter
  const filteredPayments = searchTerm
    ? payments.filter(
        (p) =>
          (p.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.transactionId || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : payments;

  const handleEdit = (payment: Payment) => {
    // Check 24-hour window
    const hours = (Date.now() - new Date(payment.createdAt).getTime()) / (1000 * 60 * 60);
    if (hours > 24) {
      showToast('error', 'Payments can only be edited within 24 hours');
      return;
    }
    if (payment.status !== 'COMPLETED' && payment.status !== 'PENDING') {
      showToast('error', 'Only pending or completed payments can be edited');
      return;
    }
    setEditPayment(payment);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return <TableListSkeleton columns={8} rows={8} title="payments" />;
  }

  if (error) {
    return (
      <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-3 text-destructive">
          <CreditCard className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Failed to load payments</p>
            <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1>Payments</h1>
          <p className="text-muted-foreground text-lg">Track and manage payments</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/reconciliation')}
            className="claude-button claude-button-secondary"
          >
            <FileText className="w-4 h-4" />
            Reconciliation
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="claude-card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Total Collected
                </p>
                <p className="text-4xl font-semibold tracking-tight text-success">
                  {formatCurrency(summary.grandTotal)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {pagination?.total || 0} payment{(pagination?.total || 0) !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-success" />
              </div>
            </div>
          </div>

          {Object.entries(summary.byMethod)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 2)
            .map(([method, total]) => (
              <div key={method} className="claude-card p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      {PAYMENT_METHOD_LABELS[method as PaymentMethod] || method}
                    </p>
                    <p className="text-4xl font-semibold tracking-tight">{formatCurrency(total)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Filters */}
      <div className="claude-card p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Search by customer, invoice #, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="claude-input pl-10 w-full"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`claude-button ${showFilters ? 'claude-button-primary' : 'claude-button-secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as PaymentStatus | 'all');
                    setPage(1);
                  }}
                  className="claude-input w-full"
                >
                  <option value="all">All Statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REFUNDED">Refunded</option>
                  <option value="PARTIALLY_REFUNDED">Partially Refunded</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Payment Method</label>
                <select
                  value={methodFilter}
                  onChange={(e) => {
                    setMethodFilter(e.target.value as PaymentMethod | 'all');
                    setPage(1);
                  }}
                  className="claude-input w-full"
                >
                  <option value="all">All Methods</option>
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="claude-input w-full"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="claude-input w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="claude-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="claude-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Invoice</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="claude-empty-state">
                      <CreditCard className="claude-empty-state-icon" />
                      <h3 className="claude-empty-state-title">No payments found</h3>
                      <p className="claude-empty-state-description">
                        {searchTerm || statusFilter !== 'all' || methodFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : 'No payments have been recorded yet'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => {
                  const statusCfg =
                    PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.PENDING;
                  return (
                    <tr key={payment.id}>
                      <td className="text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDate(payment.paidAt || payment.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold truncate text-sm">
                              {payment.customerName || '—'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="font-medium text-sm">{payment.invoiceNumber || '—'}</span>
                      </td>
                      <td>
                        <span className="font-semibold">{formatCurrency(payment.amount)}</span>
                        {payment.refundAmount && payment.refundAmount > 0 && (
                          <p className="text-xs text-destructive">
                            -{formatCurrency(payment.refundAmount)} refunded
                          </p>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-sm">
                          <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />
                          {PAYMENT_METHOD_LABELS[payment.method] || payment.method}
                        </div>
                      </td>
                      <td className="text-muted-foreground text-xs font-mono">
                        {payment.transactionId || '—'}
                      </td>
                      <td>
                        <span className={statusCfg.className}>{statusCfg.label}</span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleEdit(payment)}
                          className="claude-button claude-button-ghost text-sm p-2"
                          title="Edit payment"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="claude-button claude-button-secondary text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="claude-button claude-button-secondary text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Payment Record/Edit Modal */}
      <PaymentRecordModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditPayment(null);
        }}
        editPayment={editPayment}
        invoiceId={editPayment?.invoiceId}
        invoiceNumber={editPayment?.invoiceNumber || undefined}
      />
    </div>
  );
}
